import styled from "@emotion/styled"
import { spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import { getSaleAndDeliveryRoute } from "@slfinrtl/ui-bff-routes"
import type { SalesActivitySaleAndDeliveryView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useState } from "react"
import { defineMessages, useIntl } from "react-intl"
import { useLocation } from "react-router"
import { getFeatureToggle } from "src/common/getConfig"
import { useDealerAppRoutes } from "src/common/routes/useDealerAppRoutes"
import { ActionItem, PorscheApprovedDeliveryDowntimeAnnouncement, Stage } from "src/components"
import { useModal } from "src/context/modal/useModal"
import { useBff, useBusinessPartnerName, useConfig } from "src/hooks"
import { useVehicleDetailsContext } from "../../context/VehicleDetailsContext"
import { CancelSaleModalContainer } from "../../modal/CancelSaleModalContainer"
import { SaleAndDelivery } from "./components/saleAndDelivery/SaleAndDelivery"
import { SaleAndDeliverySkeleton } from "./components/saleAndDelivery/SaleAndDeliverySkeleton"
import { PorscheApprovedWarrantyDurationSaleActivityContainer } from "./porscheApprovedSale/PorscheApprovedWarrantyDurationSaleActivityContainer"

const messages = defineMessages({
	saleAndDeliveryHeadline: {
		defaultMessage: "Sales and Delivery",
		id: "vehicleDetails.salesActivity.saleAndDelivery.headline",
	},
})

export const SaleAndDeliveryContainer = () => {
	const [view, setView] = useState<SalesActivitySaleAndDeliveryView | undefined>(undefined)

	const { formatMessage } = useIntl()
	const { pathname } = useLocation()
	const showCancelSaleModal = useModal(CancelSaleModalContainer)
	const { porscheApprovedEnabled } = useConfig()
	const isPorscheApprovedDeliveryDowntimeAnnouncementEnabled = getFeatureToggle(
		"porsche-approved-delivery-downtime-announcement-enabled",
	)
	const { inventoryId, notify } = useVehicleDetailsContext()
	const routes = useDealerAppRoutes()
	const isInventorySalesActivityRoute = pathname === routes?.inventoryList?.vehicleDetails?.sales?.url?.(inventoryId)
	const isPorscheApprovedDeliveryDowntimeAnnouncementVisible =
		isInventorySalesActivityRoute && porscheApprovedEnabled && isPorscheApprovedDeliveryDowntimeAnnouncementEnabled

	const businessPartnerNameResponse = useBusinessPartnerName(
		view?.salesInformation?.capInformation?.capBusinessPartnerId,
	)

	const onLinkClick = useCallback((_item: string) => undefined, [])

	const fetchSaleAndDeliveryView = useBff(
		getSaleAndDeliveryRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"inline",
	)
	useEffect(() => {
		fetchSaleAndDeliveryView().then(setView)
	}, [fetchSaleAndDeliveryView])

	const onSaleCancelled = useCallback(() => {
		notify({ event: "saleCancelled", payload: undefined })
		setTimeout(() => fetchSaleAndDeliveryView().then(setView), 1000)
	}, [fetchSaleAndDeliveryView, notify])

	return !view ? (
		<Stage title={formatMessage(messages.saleAndDeliveryHeadline)}>
			<SaleAndDeliverySkeleton />
		</Stage>
	) : (
		<Stage
			title={formatMessage(messages.saleAndDeliveryHeadline)}
			actionButtons={[
				view.salesAction && routes?.inventoryList?.vehicleDetails?.sell?.url && (
					<ActionItem
						key="createSell"
						icon="purchase"
						route={routes.inventoryList.vehicleDetails.sell.url(inventoryId)}
						view={view.salesAction}
					/>
				),
				view.cancelAction && (
					<ActionItem
						key="cancleSale"
						icon="purchase"
						view={view.cancelAction}
						onClick={() =>
							showCancelSaleModal({
								inventoryId,
								offerId: view.cancelableOffer!.id,
								onSaleCancelled,
							})
						}
					/>
				),
				view.deliveryAction && routes?.inventoryList?.vehicleDetails?.deliver && (
					<ActionItem
						key="deliverVehicle"
						icon="truck"
						view={view.deliveryAction}
						route={routes.inventoryList.vehicleDetails.deliver.url(inventoryId)}
					/>
				),
			]}
		>
			<StageContent data-testid="sales-and-delivery-stage-content">
				{isPorscheApprovedDeliveryDowntimeAnnouncementVisible && (
					<PorscheApprovedDeliveryDowntimeAnnouncement hasTopMargin={false} />
				)}
				<SaleAndDelivery
					customerName={businessPartnerNameResponse?.value ?? view?.salesInformation?.customerName}
					onLinkClick={onLinkClick}
					renderPorscheApprovedWarrantyDuration={
						porscheApprovedEnabled
							? (porscheApprovedSale) => (
									<PorscheApprovedWarrantyDurationSaleActivityContainer sale={porscheApprovedSale} />
								)
							: undefined
					}
					vehicleKey={inventoryId}
					view={view}
				/>
			</StageContent>
		</Stage>
	)
}

const StageContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticMd};
`
